import React, { useState, useEffect } from "react";
// Import FaQrcode from Font Awesome (fa) to replace the unavailable FiQrCode
import { FiLock, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { FaQrcode } from "react-icons/fa"; // 🚀 Icon Fix
import toast from "react-hot-toast";
// 🚀 FIXED FUNCTION IMPORTS AND RELATIVE PATH
import { generate2FASecretForSetup, verify2FA } from "../api/auth-api";
import { QRCodeCanvas } from 'qrcode.react'; // You need to have 'qrcode.react' installed

// Define the steps within the component
const SETUP_STEP = {
  START: 'START',
  GENERATING: 'GENERATING',
  SCAN_AND_VERIFY: 'SCAN_AND_VERIFY',
  COMPLETE: 'COMPLETE',
};

// We now receive the temporary token from the login flow
const TwoFactorSetup = ({ tempToken, onClose, onLoginSuccess }) => {
  const [step, setStep] = useState(SETUP_STEP.GENERATING); // Start directly at GENERATING
  const [otpAuthUrl, setOtpAuthUrl] = useState(null); // Used for QR code
  const [manualSecret, setManualSecret] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState('');

  // 1. Generate Secret immediately upon mounting (since setup is compulsory)
  useEffect(() => {
    const startSetup = async () => {
      setError('');
      setStep(SETUP_STEP.GENERATING);
      try {
        // 🚀 CORRECTED API CALL: Using the new function name
        const res = await generate2FASecretForSetup(tempToken);
        
        setOtpAuthUrl(res.otpauthUrl); 
        setManualSecret(res.secret); 
        setStep(SETUP_STEP.SCAN_AND_VERIFY);
        
      } catch (error) {
        console.error("2FA Setup Failed:", error);
        setError(error.message || "Failed to start 2FA setup. Please try logging in again.");
        setStep(SETUP_STEP.START); // Revert to START on error
      }
    };
    if (tempToken) {
        startSetup();
    }
  }, [tempToken]);

  // 2. Finalize Enrollment (Verification)
  const finalizeSetup = async (e) => {
    e.preventDefault();
    setError('');
    
    if (confirmationCode.length !== 6) {
        setError("Please enter a valid 6-digit code.");
        return;
    }
    
    setIsConfirming(true);
    try {
      // 🚀 CORRECTED API CALL: Using the unified verify2FA function
      const res = await verify2FA(confirmationCode, tempToken);
      
      // Save final token and user info
      localStorage.setItem("token", res.token);
      localStorage.setItem("user", JSON.stringify(res.user));
      
      setStep(SETUP_STEP.COMPLETE);
      toast.success("2FA successfully activated! Access granted.");
      
      // Redirect to dashboard via parent component's success handler
      onLoginSuccess(res.user.role); 
      
    } catch (error) {
      console.error("2FA Confirmation Failed:", error);
      setError(error.message || "Invalid code. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  };


  const renderContent = () => {
    switch (step) {
      case SETUP_STEP.START:
        return (
            <div className="text-center py-8">
                <FiXCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-semibold mb-4">{error}</p>
                <button
                    onClick={onClose} // Go back to login
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition duration-200"
                >
                    Return to Login
                </button>
            </div>
        );

      case SETUP_STEP.GENERATING:
        return (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Generating secure secret and QR code...</p>
          </div>
        );

      case SETUP_STEP.SCAN_AND_VERIFY:
        return (
          <>
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-4">
                {/* 🚀 Using FaQrcode */}
                <FaQrcode className="text-orange-500" /> 2FA Setup Required
            </h3>
            <p className="text-gray-600 mb-4 text-sm">
              Mandatory Setup: Use your authenticator app to scan the QR code. Then, enter the 6-digit code to activate your account.
            </p>

            <div className="flex flex-col items-center p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
              {otpAuthUrl && (
                <div className="w-48 h-48 mb-4 border p-2 bg-white flex items-center justify-center">
                    <QRCodeCanvas value={otpAuthUrl} size={180} level="H" />
                </div>
              )}
              <p className="text-sm font-medium text-gray-700">
                Manual Secret: <span className="font-mono text-orange-600 select-all">{manualSecret}</span>
              </p>
            </div>

            <form onSubmit={finalizeSetup} className="space-y-4">
              <div>
                <label htmlFor="confirmationCode" className="block text-gray-700 mb-1 font-medium">
                  Enter 6-digit Code
                </label>
                <input
                  id="confirmationCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="e.g., 123456"
                  required
                  autoFocus
                  className="w-full px-4 py-2 text-center text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 tracking-widest"
                />
              </div>
              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  disabled={isConfirming || confirmationCode.length !== 6}
                  className="w-full px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isConfirming ? "Confirming..." : "Activate & Login"}
                </button>
              </div>
            </form>
          </>
        );

      case SETUP_STEP.COMPLETE:
        return (
          <div className="text-center py-8">
            <FiCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Setup Complete!</h3>
            <p className="text-gray-600 mb-6">Redirecting to the dashboard...</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        {renderContent()}
    </div>
  );
};

export default TwoFactorSetup;